<script setup>
// NOOVI: ponte entre o macro e os modais de Ganho/Perdido do Pipeline.
//
// Existe para não duplicar a resolução do card nos dois lugares que rodam macro
// (a lista lateral e o ReplyBox). O macro para, este componente descobre QUAL
// card fechar, abre o modal já pronto do Pipeline, e devolve o controle.
//
// Por que resolver o card aqui e não no composable: `execute()` é síncrono e os
// chamadores dependem disso. Buscar card é I/O. Manter o composable síncrono e
// pôr o I/O na camada de UI preserva o contrato que já existia.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import MarkAsWonModal from 'dashboard/routes/dashboard/pipeline/components/MarkAsWonModal.vue';
import MarkAsLostModal from 'dashboard/routes/dashboard/pipeline/components/MarkAsLostModal.vue';

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();
const store = useStore();

const dealType = ref(null);
const cards = ref([]);
const selectedCard = ref(null);
const showPicker = ref(false);
const loading = ref(false);

const showWon = computed(
  () => !!selectedCard.value && dealType.value === 'won'
);
const showLost = computed(
  () => !!selectedCard.value && dealType.value === 'lost'
);

// Um card já fechado não é candidato: o serviço tem `next if card.won?`, então
// oferecer um card fechado abriria um modal que não muda nada.
const isActionable = card =>
  dealType.value === 'won' ? !card.won_at : !card.lost_at;

const reset = () => {
  dealType.value = null;
  cards.value = [];
  selectedCard.value = null;
  showPicker.value = false;
};

const cancel = () => {
  reset();
  emit('close');
};

const open = async (tipo, conversationId) => {
  dealType.value = tipo;
  loading.value = true;

  try {
    const accountId = store.getters.getCurrentAccountId;
    const { data } = await PipelineCardsAPI.getCardByConversationId(
      conversationId,
      { accountId }
    );
    cards.value = (data || []).filter(isActionable);
  } catch (error) {
    cards.value = [];
  } finally {
    loading.value = false;
  }

  if (cards.value.length === 0) {
    useAlert(t('MACROS.EXECUTE.NO_PIPELINE_CARD'));
    cancel();
    return;
  }

  // Uma conversa pode ter mais de um card — já foi bug real. Escolher sozinha
  // fecharia o negócio errado em silêncio, então quem decide é o agente.
  if (cards.value.length > 1) {
    showPicker.value = true;
    return;
  }

  [selectedCard.value] = cards.value;
};

const pick = card => {
  selectedCard.value = card;
  showPicker.value = false;
};

const onSuccess = () => {
  reset();
  emit('submit');
};

defineExpose({ open });
</script>

<template>
  <div>
    <woot-modal
      v-if="showPicker"
      :show="showPicker"
      :on-close="cancel"
      size="medium"
    >
      <div class="flex flex-col gap-4 p-8">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ $t('MACROS.EXECUTE.PICK_PIPELINE_CARD') }}
        </h3>
        <ul class="flex flex-col gap-2 m-0 list-none">
          <li v-for="card in cards" :key="card.id">
            <button
              type="button"
              class="w-full px-4 py-3 text-start border rounded-lg border-n-weak hover:border-n-brand hover:bg-n-alpha-1"
              @click="pick(card)"
            >
              <span class="block text-sm font-medium text-n-slate-12">
                {{ card.title || card.item_details?.title }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{ card.pipeline?.name }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </woot-modal>

    <MarkAsWonModal
      v-if="showWon"
      :show="showWon"
      :item="selectedCard"
      @success="onSuccess"
      @close="cancel"
    />
    <MarkAsLostModal
      v-if="showLost"
      :show="showLost"
      :item="selectedCard"
      @success="onSuccess"
      @close="cancel"
    />
  </div>
</template>
